import { API_ENDPOINTS } from '@/config/api.config';
import {
  APIResponse,
  ApplyTemplateRequest,
  AssignCourseRequest,
  AssignmentListFilters,
  AssignmentResponse,
  AssignmentStatsResponse,
  AssignmentTemplate,
  BulkUpdateRequest,
  BulkUpdateResponse,
  BulkImportTemplate,
  BulkImportResponse,
  BulkValidationResponse,
  CloneTemplateRequest,
  CompleteTrackingRequest,
  ConflictReport,
  ConflictResolutionResult,
  CourseAssignment,
  CourseTracking,
  CreateTemplateRequest,
  ExpiringCourse,
  ExpiringCoursesFilters,
  LearningPath,
  LearningPathFilters,
  NotificationResponse,
  PaginatedResponse,
  PopularTemplatesFilters,
  RecentAssignment,
  RecentAssignmentFilters,
  ResetTrackingRequest,
  TemplateDeleteResponse,
  TemplateListFilters,
  TrackingSummary,
  UpdateAssignmentRequest,
  UpdateProgressRequest,
  UpdateTemplateRequest,
  UserProgressFilters
} from '@/types/course-assignment.types';
import apiClient from './api';

// User interface for course assignment
export interface User {
  _id: string;
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  full_name?: string;
  employee_id?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  groups?: {
    group_key: string;
    group_name: string;
    roles?: {
      role_key: string;
      role_name: string;
    }[];
  }[];
  courses?: {
    assigned?: number;
    completed?: number;
  };
  meta_data?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

// Group interface
export interface Group {
  _id: string;
  group_key: string;
  group_name: string;
  group_description?: string;
  parent_group_key?: string;
  createdAt: string;
  updatedAt: string;
}

// Role interface
export interface Role {
  _id: string;
  role_key: string;
  role_name: string;
  role_description?: string;
  createdAt: string;
  updatedAt: string;
}

// Course interface
export interface Course {
  _id: string;
  id: string;
  title: string;
  description?: string;
  thumbnail?: string;
  thumbnail_image_url?: string;
  stage?: string;
  category?: string;
  difficulty?: string;
  estimated_duration?: any;
  modules?: any[];
  module_count?: number;
  has_video_content?: boolean;
  has_audio_content?: boolean;
  created_at?: string;
  updated_at?: string;
}

// Course assignment settings
export interface CourseAssignmentSettings {
  mandatory?: boolean;
  is_mandatory?: boolean;
  deadline?: string;
  deadline_date?: string;
  send_reminders?: boolean;
  send_notification?: boolean;
  enable_automatic_reminder?: boolean;
  reminder_days?: number;
  reminder_frequency_days?: number;
  allow_retake?: boolean;
  max_attempts?: number;
  passing_score?: number;
  include_certificate?: boolean;
  notification_type?: 'email' | 'in_app' | 'both';
  priority?: 'low' | 'medium' | 'high';
  message?: string;
}

class CourseAssignmentService {
  // ==========================================
  // COURSE ASSIGNMENT ENDPOINTS
  // ==========================================

  /**
   * Assign courses to users
   * POST /skillzen-srv/api/course-assignments/assign/users
   */
  async assignCoursesToUsers(request: AssignCourseRequest): Promise<AssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: AssignmentResponse;
    }>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.ASSIGN_TO_USERS,
      request
    );
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to assign courses to users');
    }
    
    return response.data.data;
  }

  /**
   * Assign courses to groups
   * POST /skillzen-srv/api/course-assignments/assign/groups
   */
  async assignCoursesToGroups(request: AssignCourseRequest): Promise<AssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: AssignmentResponse;
    }>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.ASSIGN_TO_GROUPS,
      request
    );
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to assign courses to groups');
    }
    
    return response.data.data;
  }

  /**
   * Assign courses to roles
   * POST /skillzen-srv/api/course-assignments/assign/roles
   */
  async assignCoursesToRoles(request: AssignCourseRequest): Promise<AssignmentResponse> {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description: string;
      data: AssignmentResponse;
    }>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.ASSIGN_TO_ROLES,
      request
    );
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to assign courses to roles');
    }
    
    return response.data.data;
  }

  /**
   * List all assignments with pagination and filters
   * POST /skillzen-srv/api/course-assignments (sends filters in body)
   */
  async listAssignments(filters?: AssignmentListFilters): Promise<PaginatedResponse<CourseAssignment>> {
    const body = filters || {};

    const response = await apiClient.post<PaginatedResponse<CourseAssignment>>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.LIST,
      body
    );

    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch assignments');
    }

    return response.data;
  }

  /**
   * Get recent assignments
   * GET /skillzen-srv/api/course-assignments/recent
   */
  async getRecentAssignments(filters?: RecentAssignmentFilters): Promise<{ assignments: RecentAssignment[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams();
    
    if (filters?.page) params.set('page', filters.page.toString());
    if (filters?.limit) params.set('limit', filters.limit.toString());
    if (filters?.status) params.set('status', filters.status);
    if (filters?.is_mandatory !== undefined) params.set('is_mandatory', filters.is_mandatory.toString());
    if (filters?.assignment_type) params.set('assignment_type', filters.assignment_type);
    
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: {
        assignments: RecentAssignment[];
        total: number;
        page: number;
        limit: number;
      };
    }>(`${API_ENDPOINTS.COURSE_ASSIGNMENTS.RECENT}?${params.toString()}`);
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch recent assignments');
    }
    
    return response.data.data;
  }

  /**
   * Get assignment by ID
   * GET /skillzen-srv/api/course-assignments/{id}
   */
  async getAssignmentById(id: string): Promise<CourseAssignment> {
    const response = await apiClient.get<{
      success: boolean;
      code: number;
      description: string;
      data: CourseAssignment;
    }>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.GET_BY_ID(id)
    );
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to fetch assignment');
    }
    
    return response.data.data;
  }

  /**
   * Update assignment
   * PUT /skillzen-srv/api/course-assignments/{id}
   */
  async updateAssignment(id: string, request: UpdateAssignmentRequest): Promise<CourseAssignment> {
    const response = await apiClient.put<APIResponse<CourseAssignment>>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.UPDATE(id),
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to update assignment');
    }
    
    return response.data.result!;
  }

  /**
   * Delete/Cancel assignment
   * DELETE /skillzen-srv/api/course-assignments/{id}
   */
  async deleteAssignment(id: string): Promise<{ deleted: boolean; id: string; message: string }> {
    const response = await apiClient.delete<APIResponse<{ deleted: boolean; id: string; message: string }>>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.DELETE(id)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to delete assignment');
    }
    
    return response.data.result!;
  }

  /**
   * Revoke a GROUP or ROLE assignment
   * DELETE /skillzen-srv/api/course-assignments/revoke/assignment/{id}
   */
  async revokeAssignment(assignmentId: string): Promise<{
    assignment_deleted: number;
    course_trackings_deleted: number;
    assessment_attempts_deleted: number;
    certificates_deleted: number;
    lp_linked_tracks_skipped: number;
  }> {
    const response = await apiClient.delete(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.REVOKE_ASSIGNMENT(assignmentId)
    );
    return response.data?.data || response.data;
  }

  /**
   * Send reminder notifications for assignment
   * POST /skillzen-srv/api/course-assignments/{id}/send-reminders
   */
 async sendReminderNotifications(id: string): Promise<NotificationResponse['data']> {
  const response = await apiClient.post<APIResponse<NotificationResponse>>(
    API_ENDPOINTS.COURSE_ASSIGNMENTS.SEND_REMINDERS(id)
  );
 
  // Check API-level error
  if (response.data.status === 'error') {
    throw new Error(response.data.error?.message || 'Failed to send reminder notifications');
  }
  // Ensure inner data exists
  const reminderData = response.data.data as NotificationResponse['data'];
  if (!reminderData) {
    throw new Error('No reminder data received from API');
  }
  return reminderData;
}

  /**
   * Send deadline approaching notifications
   * POST /skillzen-srv/api/course-assignments/notifications/deadline-approaching
   */
  async sendDeadlineNotifications(daysBefore?: number): Promise<NotificationResponse> {
    const params = new URLSearchParams();
    if (daysBefore) params.set('days_before', daysBefore.toString());
    
    const response = await apiClient.post<APIResponse<NotificationResponse>>(
      `${API_ENDPOINTS.COURSE_ASSIGNMENTS.DEADLINE_NOTIFICATIONS}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to send deadline notifications');
    }
    
    return response.data.result!;
  }

  /**
   * Retry failed notifications
   * POST /skillzen-srv/api/course-assignments/notifications/retry-failed
   */
  async retryFailedNotifications(): Promise<NotificationResponse> {
    const response = await apiClient.post<APIResponse<NotificationResponse>>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.RETRY_FAILED_NOTIFICATIONS
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to retry failed notifications');
    }
    
    return response.data.result!;
  }

  /**
   * Update assignment statistics
   * PUT /skillzen-srv/api/course-assignments/{id}/stats
   */
  async updateAssignmentStats(id: string): Promise<AssignmentStatsResponse> {
    const response = await apiClient.put<APIResponse<AssignmentStatsResponse>>(
      API_ENDPOINTS.COURSE_ASSIGNMENTS.UPDATE_STATS(id)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to update assignment statistics');
    }
    
    return response.data.result!;
  }

  // ==========================================
  // COURSE TRACKING ENDPOINTS
  // ==========================================

  /**
   * Get user course progress
   * GET /skillzen-srv/api/course-tracking/user/{userId}/progress
   */
  async getUserProgress(userId: string, filters?: UserProgressFilters): Promise<CourseTracking[]> {
    const params = new URLSearchParams();
    if (filters?.course_id) params.set('course_id', filters.course_id);
    if (filters?.status) params.set('status', filters.status);
    
    const response = await apiClient.get<APIResponse<CourseTracking[]>>(
      `${API_ENDPOINTS.COURSE_TRACKING.USER_PROGRESS(userId)}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch user progress');
    }
    
    return response.data.result!;
  }

  /**
   * Update course progress
   * PUT /skillzen-srv/api/course-tracking/{trackingId}/progress
   */
  async updateProgress(trackingId: string, request: UpdateProgressRequest): Promise<CourseTracking> {
    const response = await apiClient.put<APIResponse<CourseTracking>>(
      API_ENDPOINTS.COURSE_TRACKING.UPDATE_PROGRESS(trackingId),
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to update progress');
    }
    
    return response.data.result!;
  }

  /**
   * Mark course as complete
   * POST /skillzen-srv/api/course-tracking/{trackingId}/complete
   */
  async completeCourse(trackingId: string, request?: CompleteTrackingRequest): Promise<CourseTracking> {
    const response = await apiClient.post<APIResponse<CourseTracking>>(
      API_ENDPOINTS.COURSE_TRACKING.COMPLETE_COURSE(trackingId),
      request || {}
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to complete course');
    }
    
    return response.data.result!;
  }

  /**
   * Get assignment tracking summary
   * GET /skillzen-srv/api/course-tracking/assignment/{assignmentId}/summary
   */
  async getAssignmentSummary(assignmentId: string): Promise<TrackingSummary> {
    const response = await apiClient.get<APIResponse<TrackingSummary>>(
      API_ENDPOINTS.COURSE_TRACKING.ASSIGNMENT_SUMMARY(assignmentId)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch assignment summary');
    }
    
    return response.data.result!;
  }

  /**
   * Get user learning path
   * GET /skillzen-srv/api/course-tracking/user/{userId}/learning-path
   */
  async getUserLearningPath(userId: string, filters?: LearningPathFilters): Promise<LearningPath> {
    const params = new URLSearchParams();
    if (filters?.include_completed !== undefined) params.set('include_completed', filters.include_completed.toString());
    if (filters?.sort_by) params.set('sort_by', filters.sort_by);
    
    const response = await apiClient.get<APIResponse<LearningPath>>(
      `${API_ENDPOINTS.COURSE_TRACKING.USER_LEARNING_PATH(userId)}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch learning path');
    }
    
    return response.data.result!;
  }

  /**
   * Reset course progress
   * POST /skillzen-srv/api/course-tracking/{trackingId}/reset
   */
  async resetProgress(trackingId: string, request: ResetTrackingRequest): Promise<CourseTracking> {
    const response = await apiClient.post<APIResponse<CourseTracking>>(
      API_ENDPOINTS.COURSE_TRACKING.RESET_PROGRESS(trackingId),
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to reset progress');
    }
    
    return response.data.result!;
  }

  /**
   * Bulk update progress
   * POST /skillzen-srv/api/course-tracking/bulk-update
   */
  async bulkUpdateProgress(request: BulkUpdateRequest): Promise<BulkUpdateResponse> {
    const response = await apiClient.post<APIResponse<BulkUpdateResponse>>(
      API_ENDPOINTS.COURSE_TRACKING.BULK_UPDATE,
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to bulk update progress');
    }
    
    return response.data.result!;
  }

  /**
   * Get expiring courses
   * GET /skillzen-srv/api/course-tracking/expiring
   */
  async getExpiringCourses(filters?: ExpiringCoursesFilters): Promise<ExpiringCourse[]> {
    const params = new URLSearchParams();
    if (filters?.days_ahead) params.set('days_ahead', filters.days_ahead.toString());
    if (filters?.include_expired !== undefined) params.set('include_expired', filters.include_expired.toString());
    
    const response = await apiClient.get<APIResponse<ExpiringCourse[]>>(
      `${API_ENDPOINTS.COURSE_TRACKING.EXPIRING_COURSES}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch expiring courses');
    }
    
    return response.data.result!;
  }

  // ==========================================
  // ASSIGNMENT CONFLICTS ENDPOINTS
  // ==========================================

  /**
   * Get user conflict report
   * GET /skillzen-srv/api/assignment-conflicts/user/{userId}/report
   */
  async getUserConflictReport(userId: string): Promise<ConflictReport> {
    const response = await apiClient.get<APIResponse<ConflictReport>>(
      API_ENDPOINTS.ASSIGNMENT_CONFLICTS.USER_REPORT(userId)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch conflict report');
    }
    
    return response.data.result!;
  }

  /**
   * Resolve user course conflicts
   * POST /skillzen-srv/api/assignment-conflicts/user/{userId}/course/{courseId}/resolve
   */
  async resolveUserCourseConflicts(userId: string, courseId: string): Promise<CourseTracking | ConflictResolutionResult> {
    const response = await apiClient.post<APIResponse<CourseTracking | ConflictResolutionResult>>(
      API_ENDPOINTS.ASSIGNMENT_CONFLICTS.RESOLVE_USER_COURSE(userId, courseId)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to resolve conflicts');
    }
    
    return response.data.result!;
  }

  // ==========================================
  // ASSIGNMENT TEMPLATES ENDPOINTS
  // ==========================================

  /**
   * Create assignment template
   * POST /skillzen-srv/api/assignment-templates
   */
  async createTemplate(request: CreateTemplateRequest): Promise<AssignmentTemplate> {
    const response = await apiClient.post<APIResponse<AssignmentTemplate>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.CREATE,
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to create template');
    }
    
    return response.data.result!;
  }

  /**
   * Update assignment template
   * PUT /skillzen-srv/api/assignment-templates/{id}
   */
  async updateTemplate(id: string, request: UpdateTemplateRequest): Promise<AssignmentTemplate> {
    const response = await apiClient.put<APIResponse<AssignmentTemplate>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.UPDATE(id),
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to update template');
    }
    
    return response.data.result!;
  }

  /**
   * Get template by ID
   * GET /skillzen-srv/api/assignment-templates/{id}
   */
  async getTemplateById(id: string): Promise<AssignmentTemplate> {
    const response = await apiClient.get<APIResponse<AssignmentTemplate>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.GET_BY_ID(id)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch template');
    }
    
    return response.data.result!;
  }

  /**
   * Get template by key
   * GET /skillzen-srv/api/assignment-templates/key/{key}
   */
  async getTemplateByKey(key: string): Promise<AssignmentTemplate> {
    const response = await apiClient.get<APIResponse<AssignmentTemplate>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.GET_BY_KEY(key)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch template');
    }
    
    return response.data.result!;
  }

  /**
   * List all templates
   * GET /skillzen-srv/api/assignment-templates
   */
  async listTemplates(filters?: TemplateListFilters): Promise<AssignmentTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    if (filters?.is_active !== undefined) params.set('is_active', filters.is_active.toString());
    
    const response = await apiClient.get<APIResponse<AssignmentTemplate[]>>(
      `${API_ENDPOINTS.ASSIGNMENT_TEMPLATES.LIST}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch templates');
    }
    
    return response.data.result!;
  }

  /**
   * Get popular templates
   * GET /skillzen-srv/api/assignment-templates/popular/list
   */
  async getPopularTemplates(filters?: PopularTemplatesFilters): Promise<AssignmentTemplate[]> {
    const params = new URLSearchParams();
    if (filters?.limit) params.set('limit', filters.limit.toString());
    
    const response = await apiClient.get<APIResponse<AssignmentTemplate[]>>(
      `${API_ENDPOINTS.ASSIGNMENT_TEMPLATES.POPULAR}?${params.toString()}`
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to fetch popular templates');
    }
    
    return response.data.result!;
  }

  /**
   * Apply template
   * POST /skillzen-srv/api/assignment-templates/apply
   */
  async applyTemplate(request: ApplyTemplateRequest): Promise<CourseAssignment> {
    const response = await apiClient.post<APIResponse<CourseAssignment>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.APPLY,
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to apply template');
    }
    
    return response.data.result!;
  }

  /**
   * Clone template
   * POST /skillzen-srv/api/assignment-templates/{id}/clone
   */
  async cloneTemplate(id: string, request: CloneTemplateRequest): Promise<AssignmentTemplate> {
    const response = await apiClient.post<APIResponse<AssignmentTemplate>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.CLONE(id),
      request
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to clone template');
    }
    
    return response.data.result!;
  }

  /**
   * Delete template
   * DELETE /skillzen-srv/api/assignment-templates/{id}
   */
  async deleteTemplate(id: string): Promise<TemplateDeleteResponse> {
    const response = await apiClient.delete<APIResponse<TemplateDeleteResponse>>(
      API_ENDPOINTS.ASSIGNMENT_TEMPLATES.DELETE(id)
    );
    
    if (response.data.status === 'error') {
      throw new Error(response.data.error?.message || 'Failed to delete template');
    }
    
    return response.data.result!;
  }

  // ==========================================
  // BULK IMPORT ENDPOINTS
  // ==========================================

  /**
   * Get CSV template for bulk import
   * GET /api/course-assignments/bulk/template
   */
  async getBulkImportTemplate(): Promise<BulkImportTemplate> {
    const response = await apiClient.get<{
      success: boolean;
      statusCode: number;
      description: string;
      data: BulkImportTemplate;
    }>(API_ENDPOINTS.COURSE_ASSIGNMENTS.BULK_TEMPLATE);

    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to get CSV template');
    }

    return response.data.data;
  }

  /**
   * Import bulk assignments from CSV
   * POST /api/course-assignments/bulk/import
   */
async importBulkAssignments(csvContent: string, isBase64: boolean = false): Promise<BulkImportResponse> {
  try {
    const response = await apiClient.post<{
      success: boolean;
      statusCode: number;
      description: string;
      data: BulkImportResponse;
      error?: { error_description?: string };
    }>(API_ENDPOINTS.COURSE_ASSIGNMENTS.BULK_IMPORT, {
      csv_content: csvContent,
      is_base_64: isBase64
    });

    if (!response.data.success) {
      const detailedError = response.data.error?.error_description || response.data.description;
      throw new Error(detailedError || 'The import process failed.');
    }
    return response.data.data;

  } catch (error: any) {
    console.error("API Error in importBulkAssignments:", error.response?.data || error.message);
    const errorMessage = 
      error?.response?.data?.error?.error_description || 
      error?.response?.data?.description ||             
      error?.message ||                                  
      'An unknown error occurred during the import.';   

    throw new Error(errorMessage);
  }
}

  /**
   * Validate CSV before import
   * POST /api/course-assignments/bulk/validate
   */
  async validateBulkImport(csvContent: string, isBase64: boolean = false): Promise<BulkValidationResponse> {
    const response = await apiClient.post<{
      success: boolean;
      statusCode: number;
      description: string;
      data: BulkValidationResponse;
    }>(API_ENDPOINTS.COURSE_ASSIGNMENTS.BULK_VALIDATE, {
      csv_content: csvContent,
      is_base64: isBase64
    });
    
    if (!response.data.success) {
      throw new Error(response.data.description || 'Failed to validate CSV');
    }
    
    return response.data.data;
  }

  // ==========================================
  // UTILITY METHODS
  // ==========================================

  /**
   * Get all assignments for a specific course
   */
  async getAssignmentsForCourse(courseId: string): Promise<CourseAssignment[]> {
    const response = await this.listAssignments({ limit: 100 });
    
    return response.result.filter(assignment => 
      assignment.course_ids.includes(courseId)
    );
  }

  /**
   * Get all active assignments for a user (combining USER, GROUP, and ROLE assignments)
   */
  async getUserActiveAssignments(userId: string): Promise<CourseAssignment[]> {
    try {
      const userProgress = await this.getUserProgress(userId);
      const assignmentIds = userProgress.map(track => track.assignment_id);
      
      // Fetch assignment details for each tracking record
      const assignments = await Promise.all(
        assignmentIds.map(id => this.getAssignmentById(id))
      );
      
      return assignments.filter(assignment => 
        assignment.status === 'ACTIVE'
      );
    } catch (error) {
      console.error('Failed to fetch user active assignments:', error);
      return [];
    }
  }

  /**
   * Get assignment completion statistics
   */
  async getAssignmentCompletionStats(assignmentId: string) {
    try {
      const summary = await this.getAssignmentSummary(assignmentId);
      const assignment = await this.getAssignmentById(assignmentId);
      
      return {
        assignment,
        summary,
        completionRate: summary.completion_rate,
        totalAssignees: assignment.total_assignees,
        completedCount: assignment.completed_count,
        inProgressCount: assignment.in_progress_count,
        notStartedCount: assignment.not_started_count
      };
    } catch (error) {
      console.error('Failed to fetch assignment completion stats:', error);
      throw error;
    }
  }

  /**
   * Check for conflicts before creating assignment
   */
  async checkForConflicts(userId: string, courseId: string): Promise<boolean> {
    try {
      const report = await this.getUserConflictReport(userId);
      return report.conflicts.some(conflict => conflict.course_id === courseId);
    } catch (error) {
      console.error('Failed to check for conflicts:', error);
      return false;
    }
  }

  /**
   * Check for duplicate assignments using the new API endpoint
   * Handles individual users, groups, and roles
   */
  async checkForDuplicates(params: {
    type: 'users' | 'groups' | 'roles';
    targetIds: string[];
    courseIds: string[];
  }): Promise<{
    hasConflicts: boolean;
    conflicts: Array<{
      targetId: string;
      targetType: string;
      courseId: string;
      conflictType: string;
      existingAssignments?: any[];
      users?: string[]; // For group/role assignments
    }>;
    summary: {
      totalChecked: number;
      totalConflicts: number;
      conflictsByType: Record<string, number>;
    };
  }> {
    try {
      const response = await apiClient.post<{
        status: string;
        description?: string;
        result: {
          has_conflicts: boolean;
          conflicts: Array<{
            target_id: string;
            target_type: string;
            course_id: string;
            conflict_type: string;
            existing_assignments?: any[];
            users?: string[];
          }>;
          summary: {
            total_checked: number;
            total_conflicts: number;
            conflicts_by_type: Record<string, number>;
          };
        };
      }>(API_ENDPOINTS.COURSE_ASSIGNMENTS.CHECK_DUPLICATES, {
        type: params.type,
        target_ids: params.targetIds,
        course_ids: params.courseIds
      });

      if (response.data.success && response.data.data) {
        // Transform snake_case response to camelCase - note: data is wrapped in 'data' field
        const result = response.data.data;
        return {
          hasConflicts: result.has_conflicts,
          conflicts: result.conflicts.map(conflict => ({
            targetId: conflict.target_id,
            targetType: conflict.target_type,
            courseId: conflict.course_id,
            conflictType: conflict.conflict_type,
            existingAssignments: conflict.existing_assignments,
            users: conflict.users
          })),
          summary: {
            totalChecked: result.summary.total_checked,
            totalConflicts: result.summary.total_conflicts,
            conflictsByType: result.summary.conflicts_by_type
          }
        };
      }

      // Default response if API doesn't return expected format
      return {
        hasConflicts: false,
        conflicts: [],
        summary: {
          totalChecked: 0,
          totalConflicts: 0,
          conflictsByType: {}
        }
      };
    } catch (error) {
      console.error('Failed to check for duplicates:', error);
      return {
        hasConflicts: false,
        conflicts: [],
        summary: {
          totalChecked: 0,
          totalConflicts: 0,
          conflictsByType: {}
        }
      };
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * Use checkForDuplicates() instead
   */
  async checkForDuplicateAssignments(params: {
    type: 'users' | 'groups' | 'roles';
    targetIds: string[];
    courseIds: string[];
  }): Promise<{
    hasConflicts: boolean;
    conflicts: Array<{
      targetId: string;
      targetType: string;
      courseId: string;
      conflictType: string;
      existingAssignment?: any;
      users?: string[]; // For group/role assignments
    }>;
    summary: {
      totalChecked: number;
      totalConflicts: number;
      conflictsByType: Record<string, number>;
    };
  }> {
    const conflicts: Array<{
      targetId: string;
      targetType: string;
      courseId: string;
      conflictType: string;
      existingAssignment?: any;
      users?: string[];
    }> = [];
    
    const conflictsByType: Record<string, number> = {};
    let totalChecked = 0;

    try {
      if (params.type === 'users') {
        // Check each user against each course
        for (const userId of params.targetIds) {
          for (const courseId of params.courseIds) {
            totalChecked++;
            
            try {
              // Get user conflict report
              const report = await this.getUserConflictReport(userId);
              
              // Check if this course has conflicts
              const courseConflicts = report.conflicts.filter(
                conflict => conflict.course_id === courseId
              );
              
              if (courseConflicts.length > 0) {
                courseConflicts.forEach(conflict => {
                  conflicts.push({
                    targetId: userId,
                    targetType: 'user',
                    courseId: courseId,
                    conflictType: conflict.type || 'duplicate',
                    existingAssignment: conflict
                  });
                  
                  const type = conflict.type || 'duplicate';
                  conflictsByType[type] = (conflictsByType[type] || 0) + 1;
                });
              }
            } catch (error) {
              console.error(`Failed to check conflicts for user ${userId}:`, error);
            }
          }
        }
      } else if (params.type === 'groups') {
        // For group assignments, get users in each group
        const dataService = new CourseAssignmentDataService();
        
        for (const groupId of params.targetIds) {
          try {
            // Get users in this group
            const usersResponse = await dataService.loadUsers({
              group_key: groupId,
              limit: 1000
            });
            
            const usersInGroup = usersResponse.data?.data || [];
            const userIds = usersInGroup.map(user => user._id);
            
            // Check each user in the group for each course
            for (const courseId of params.courseIds) {
              const groupConflictUsers: string[] = [];
              
              for (const userId of userIds) {
                totalChecked++;
                
                try {
                  const report = await this.getUserConflictReport(userId);
                  const hasConflict = report.conflicts.some(
                    conflict => conflict.course_id === courseId
                  );
                  
                  if (hasConflict) {
                    groupConflictUsers.push(userId);
                  }
                } catch (error) {
                  console.error(`Failed to check conflicts for user ${userId} in group ${groupId}:`, error);
                }
              }
              
              if (groupConflictUsers.length > 0) {
                conflicts.push({
                  targetId: groupId,
                  targetType: 'group',
                  courseId: courseId,
                  conflictType: 'group_user_duplicate',
                  users: groupConflictUsers
                });
                
                conflictsByType['group_user_duplicate'] = 
                  (conflictsByType['group_user_duplicate'] || 0) + groupConflictUsers.length;
              }
            }
          } catch (error) {
            console.error(`Failed to get users for group ${groupId}:`, error);
          }
        }
      } else if (params.type === 'roles') {
        // For role assignments, get users with each role
        const dataService = new CourseAssignmentDataService();
        
        for (const roleId of params.targetIds) {
          try {
            // Get users with this role
            const usersResponse = await dataService.loadUsers({
              role_key: roleId,
              limit: 1000
            });
            
            const usersWithRole = usersResponse.data?.data || [];
            const userIds = usersWithRole.map(user => user._id);
            
            // Check each user with the role for each course
            for (const courseId of params.courseIds) {
              const roleConflictUsers: string[] = [];
              
              for (const userId of userIds) {
                totalChecked++;
                
                try {
                  const report = await this.getUserConflictReport(userId);
                  const hasConflict = report.conflicts.some(
                    conflict => conflict.course_id === courseId
                  );
                  
                  if (hasConflict) {
                    roleConflictUsers.push(userId);
                  }
                } catch (error) {
                  console.error(`Failed to check conflicts for user ${userId} with role ${roleId}:`, error);
                }
              }
              
              if (roleConflictUsers.length > 0) {
                conflicts.push({
                  targetId: roleId,
                  targetType: 'role',
                  courseId: courseId,
                  conflictType: 'role_user_duplicate',
                  users: roleConflictUsers
                });
                
                conflictsByType['role_user_duplicate'] = 
                  (conflictsByType['role_user_duplicate'] || 0) + roleConflictUsers.length;
              }
            }
          } catch (error) {
            console.error(`Failed to get users for role ${roleId}:`, error);
          }
        }
      }

      return {
        hasConflicts: conflicts.length > 0,
        conflicts,
        summary: {
          totalChecked,
          totalConflicts: conflicts.length,
          conflictsByType
        }
      };
    } catch (error) {
      console.error('Failed to check for duplicate assignments:', error);
      return {
        hasConflicts: false,
        conflicts: [],
        summary: {
          totalChecked,
          totalConflicts: 0,
          conflictsByType: {}
        }
      };
    }
  }

  /**
   * Batch check for conflicts across multiple users efficiently
   */
  async batchCheckUserConflicts(
    userIds: string[],
    courseIds: string[]
  ): Promise<Map<string, Map<string, boolean>>> {
    const conflictMap = new Map<string, Map<string, boolean>>();
    
    // Initialize the map
    for (const userId of userIds) {
      conflictMap.set(userId, new Map<string, boolean>());
    }
    
    // Batch process users in chunks to avoid overwhelming the API
    const BATCH_SIZE = 10;
    for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
      const batch = userIds.slice(i, i + BATCH_SIZE);
      
      await Promise.all(
        batch.map(async (userId) => {
          try {
            const report = await this.getUserConflictReport(userId);
            const userConflictMap = conflictMap.get(userId)!;
            
            for (const courseId of courseIds) {
              const hasConflict = report.conflicts.some(
                conflict => conflict.course_id === courseId
              );
              userConflictMap.set(courseId, hasConflict);
            }
          } catch (error) {
            console.error(`Failed to check conflicts for user ${userId}:`, error);
            // Set all courses as no conflict on error
            const userConflictMap = conflictMap.get(userId)!;
            courseIds.forEach(courseId => userConflictMap.set(courseId, false));
          }
        })
      );
    }
    
    return conflictMap;
  }
}

export class CourseAssignmentDataService {
  /**
   * Load courses from API
   */
  async loadCourses(params?: {
    page?: number;
    limit?: number;
    search?: string;
    isPublished?: boolean;
  }): Promise<APIResponse<Course[]>> {
    try {
      const response = await apiClient.get<APIResponse<Course[]>>(
        API_ENDPOINTS.COURSES.LIST,
        { params: { limit: 100, isPublished: true, ...params } }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error loading courses:', error);
      throw error;
    }
  }

  /**
   * Load users from API
   */ 
  async loadUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    group_key?: string;
    role_key?: string;
    skip?: number;
  }): Promise<APIResponse<{ data: User[]; total: number; page: number; limit: number }>> {
    try {
      const response = await apiClient.post<{
        success: boolean;
        code: number;
        data: {
          data: User[];
          total: number;
          page: number;
          limit: number;
        };
        meta_data: string;
      }>(
        API_ENDPOINTS.USERS.LIST,
        { limit: 100, ...params }
      );
      return {
        status: response.data.success ? 'success' : 'error',
        data: response.data.data
      } as APIResponse<{ data: User[]; total: number; page: number; limit: number }>;
    } catch (error: any) {
      console.error('Error loading users:', error);
      throw error;
    }
  }

  /**
   * Load groups from API
   * Likely same schema as roles: { success, code, description, data: Group[], meta_data: { total_records, ... } }
   */
async loadGroups(
  body?: { skip?: number; limit?: number; group_name?: string }
): Promise<APIResponse<{ groups: Group[]; total: number }>> {
  try {
    const response = await apiClient.post<{
      success: boolean;
      code: number;
      description?: string;
      data: Group[] | { groups: Group[]; total?: number };
      meta_data?: {
        total_records: number;
        current_page?: number;
        records_per_page?: string;
      };
    }>(API_ENDPOINTS.GROUPS.LIST, body || {});

    let groups: Group[] = [];
    let total = 0;

    if (response.data.data) {
      if (Array.isArray(response.data.data)) {
        groups = response.data.data;
        total =
          response.data.meta_data?.total_records ||
          response.data.data.length;
      } else if ("groups" in response.data.data) {
        groups = response.data.data.groups || [];
        total =
          response.data.data.total ||
          response.data.meta_data?.total_records ||
          groups.length;
      }
    }

    return {
      status: response.data.success ? "success" : "error",
      data: { groups, total }
    } as APIResponse<{ groups: Group[]; total: number }>;
  } catch (error: any) {
    console.error("Error loading groups:", error);
    throw error;
  }
}


  /**
   * Load roles from API
   * Response schema: { success, code, description, data: Role[], meta_data: { total_records, ... } }
   */
  async loadRoles(params?: {
    skip?: number;
    limit?: number;
    role_name?: string;
  }): Promise<APIResponse<{ roles: Role[]; total: number }>> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        code: number;
        description: string;
        data: Role[];
        meta_data: {
          total_records: number;
          current_page: number;
          records_per_page: string;
        };
      }>(
        API_ENDPOINTS.ROLES.LIST,
        { params: { limit: 100, ...params } }
      );
      
      return {
        status: response.data.success ? 'success' : 'error',
        data: {
          roles: response.data.data || [],
          total: response.data.meta_data?.total_records || response.data.data?.length || 0
        }
      } as APIResponse<{ roles: Role[]; total: number }>;
    } catch (error: any) {
      console.error('Error loading roles:', error);
      throw error;
    }
  }
}

export const courseAssignmentService = new CourseAssignmentService();
export const courseAssignmentDataService = new CourseAssignmentDataService();